import { useEffect, useState, useCallback, useMemo, useRef, createContext, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/client/lib/auth";
import { api } from "@/client/lib/api";
import { LayersIcon } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { PortalNav } from "./PortalNav";
import { PortalHeader } from "./PortalHeader";
import { SaveToast } from "./SaveToast";
import { SectionLockBanner } from "./SectionLockBanner";
import { stepIconKind, StepCircle } from "./stepper-icon";
import { usePortalBootstrap, prefetchOverview, usePortalPreflight, portalQueries, readPortalCache, writePortalCache, flowFlags } from "@/client/lib/portal";
import { invalidateSchool } from "@/client/lib/query";
import { usePreviewMode } from "@/client/lib/preview";
import {
  isSectionCode,
  isCompleted,
  canOpenSection,
  getCanonicalSection,
  getNextRequired,
  sectionToApiCode,
  deriveSectionStatus,
  deriveGegevensSectionStatus,
  type SectionCode,
  type SectionMeta,
  type SectionStatus,
} from "./portal-flow";

// Re-export for consumers
export type { SectionCode, SectionMeta, SectionStatus };
export type PortalPhase = "data" | "waiting" | "selection" | "complete";

type SchoolInfo = {
  schoolId: number;
  schoolName: string;
  shortName: string | null;
};

type SaveState = "idle" | "saving" | "saved" | "error";

type PortalContextType = {
  schoolId: number;
  schoolInfo: SchoolInfo | null;
  phase: PortalPhase;
  sections: SectionMeta[];
  activeSection: SectionCode | null;
  saveState: SaveState;
  lastSavedAt: string | null;
  setSaveState: (state: SaveState) => void;
  setLastSavedAt: (time: string) => void;
  refreshSections: () => Promise<void>;
  goToNextSection: () => void;
  completeSection: () => void;
  getSectionLock: (apiCode: string) => SectionLock;
  // The primary button for the active section: confirm it when not yet done, otherwise
  // jump to the next unfinished step (or the overview when everything is done).
  primaryAction: { label: string; onClick: () => void; isConfirm: boolean };
};

// A section is only ever "locked" because it has not opened yet (opensAt in the
// future). Schools edit and resubmit freely otherwise — there is no closed/locked
// state and no request-to-reopen flow.
export type SectionLock = {
  locked: boolean;
  reason: "not_open" | null;
};

const PortalContext = createContext<PortalContextType | null>(null);

export function usePortal() {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error("usePortal must be used within PortalShell");
  return ctx;
}

// ─── Section labels & next-step text ───

const SECTION_TITLES: Record<SectionCode, string> = {
  contactgegevens: "Contactgegevens en schooladres",
  groepen: "Groepen en leerlingaantallen",
  studiedagen: "Studiedagen",
  controleren: "Controleren en aanleveren",
};

const SECTION_SHORT_TITLES: Record<SectionCode, string> = {
  contactgegevens: "Contactgegevens",
  groepen: "Groepen",
  studiedagen: "Studiedagen",
  controleren: "Controleren",
};

const SECTION_NEXT_LABEL: Record<SectionCode, string> = {
  contactgegevens: "Bevestigen en doorgaan",
  groepen: "Bevestigen en doorgaan",
  studiedagen: "Bevestigen en doorgaan",
  controleren: "Indienen",
};

export function getNextLabel(code: SectionCode): string {
  return SECTION_NEXT_LABEL[code];
}

/** The Extra-sluitingsdagen section (school-wide closure days). Status comes from the
 *  section's own answer state, and a "later" answer is flagged so the stepper shows a clock
 *  instead of a green check. The per-group vrije dagdelen + vakdocenten moved into the group
 *  dialog (Groepen step) and are no longer separate, gating sections. */
function buildSluitingsdagenSection(progress: any): SectionMeta {
  const steps: any[] = progress?.steps ?? [];
  const avail = progress?.beschikbaarheid ?? {};
  const choice: string = avail.studiedagen?.choice ?? "";
  const count: number = avail.studiedagen?.count ?? 0;
  const step = steps.find((s: any) => s.sectionCode === "closure_days");
  const answered = choice !== "";
  const status = deriveGegevensSectionStatus(step, answered, answered);
  const summary =
    choice === "yes"
      ? `${count} studiedag${count !== 1 ? "en" : ""}`
      : choice === "no"
        ? "Geen, niet van toepassing"
        : choice === "later"
          ? "Wordt later ingevuld"
          : "Nog niet ingevuld";
  return {
    code: "studiedagen",
    title: SECTION_TITLES.studiedagen,
    summary,
    status,
    deliveryState: step?.deliveryState ?? undefined,
    pendingLater: choice === "later",
    deadline: step?.closesAt ?? undefined,
  };
}

/** Pure builder: derive the stepper sections from a /progress payload. Used both by the
 *  async refresh and to seed initial state synchronously from the prefetched cache. */
function buildGegevensSections(progress: any): SectionMeta[] {
  const steps: any[] = progress?.steps ?? [];
  const counts = progress?.counts ?? {};
  const allYearLevelsResolved = progress?.allYearLevelsResolved ?? false;
  const hasDirecteur = progress?.hasDirecteur ?? false;
  const schoolInfoComplete = progress?.schoolInfoComplete ?? false;
  const schoolInfoStep = steps.find((s: any) => s.sectionCode === "school_info");
  const classStep = steps.find((s: any) => s.sectionCode === "class_config");
  return [
    {
      code: "contactgegevens",
      title: SECTION_TITLES.contactgegevens,
      summary: counts.contacts > 0 ? `${counts.contacts} ${counts.contacts === 1 ? "contactpersoon" : "contactpersonen"} ingevuld` : "Nog niet ingevuld",
      // Complete = at least one contact, a directeur, and the school's bezoekadres/wijk/telefoon.
      status: deriveGegevensSectionStatus(schoolInfoStep, counts.contacts > 0 && hasDirecteur && schoolInfoComplete, counts.contacts > 0 || schoolInfoComplete),
      deliveryState: schoolInfoStep?.deliveryState ?? undefined,
      deadline: schoolInfoStep?.closesAt ?? undefined,
    },
    {
      code: "groepen",
      title: SECTION_TITLES.groepen,
      summary: counts.classGroups > 0 ? `${counts.classGroups} groep${counts.classGroups !== 1 ? "en" : ""} ingevuld` : "Nog niet ingevuld",
      // Complete = at least one group AND every year level either covered or explicitly excluded.
      status: deriveGegevensSectionStatus(classStep, counts.classGroups > 0 && allYearLevelsResolved, counts.classGroups > 0),
      deliveryState: classStep?.deliveryState ?? undefined,
      deadline: classStep?.closesAt ?? undefined,
    },
    buildSluitingsdagenSection(progress),
    {
      code: "controleren",
      title: SECTION_TITLES.controleren,
      summary: "",
      status: deriveSectionStatus(steps.find((s: any) => s.sectionCode === "review"), false),
    },
  ];
}

// ─── Main Shell ───

export function SchoolPortalShell({
  renderSection,
}: {
  renderSection: (code: SectionCode, schoolId: number) => React.ReactNode;
}) {
  const { schoolId: scIdParam, section: sectionParam } = useParams<{
    schoolId: string;
    section?: string;
  }>();
  const schoolId = Number(scIdParam);
  const requestedSection = isSectionCode(sectionParam) ? sectionParam : null;

  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const isPreview = usePreviewMode();

  // Warm the shared bootstrap cache (school name + flow state) so the nav shows the
  // name instantly and the Controleren step reads its state without its own fetch.
  const { data: bootstrap } = usePortalBootstrap(schoolId);
  usePortalPreflight(schoolId);
  // Whole-flow delivery state, used for the Controleren step's icon (green=aangeleverd,
  // amber=gewijzigd). The data steps carry their own per-section deliveryState.
  const gegFlow = bootstrap ? flowFlags(bootstrap.flows.schoolgegevens) : null;
  const controlerenDelivery: "no_version" | "delivered" | "changed" =
    !gegFlow?.everSubmitted ? "no_version" : gegFlow.hasUnsubmittedChanges ? "changed" : "delivered";

  // Seed the stepper from the prefetched /progress cache so the shell renders instantly
  // (no skeleton) when navigating in; refreshSections still revalidates in the background.
  const cachedProgress = readPortalCache<any>(portalQueries.progress(schoolId).queryKey);
  const seededSections = cachedProgress ? buildGegevensSections(cachedProgress) : null;
  // Whether the school already has data, derived synchronously from the seeded cache.
  // null = unknown (cold cache); we use it to pick the right first paint below.
  const seededHasData = seededSections
    ? seededSections.some((s) => isCompleted(s.status) || s.status === "in_progress")
    : null;

  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null);
  const [sections, setSections] = useState<SectionMeta[]>(() => seededSections ?? []);
  const [phase] = useState<PortalPhase>("data");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => !cachedProgress);
  const [loadError, setLoadError] = useState(false);
  // Decide welcome-vs-stepper on the FIRST paint from the seeded cache, so a blank school
  // never flashes the section UI before the welcome card (or vice versa). `welcomeDismissed`
  // keeps "Beginnen" sticky so a background/post-save refresh cannot make the welcome
  // reappear while the school is entering its first data.
  const [showWelcome, setShowWelcome] = useState<boolean>(() => seededHasData === false);
  const welcomeDismissed = useRef(false);
  const [steps, setSteps] = useState<any[]>(() => cachedProgress?.steps ?? []);

  const refreshSections = useCallback(async () => {
    try {
      setLoadError(false);
      const schoolsRes = await (api.api.school as any)["my-schools"].$get();
      if (schoolsRes.ok) {
        const data = await schoolsRes.json();
        const schools = (data as any).schools ?? [];
        const school = schools.find((s: any) => s.schoolId === schoolId);
        if (school) {
          setSchoolInfo({
            schoolId: school.schoolId,
            schoolName: school.shortName || school.schoolName,
            shortName: school.shortName,
          });
        }
      }

      const progressRes = await fetch(`/api/school/${schoolId}/progress`, { credentials: "include" });
      if (progressRes.ok) {
        const progress = await progressRes.json();
        writePortalCache(portalQueries.progress(schoolId).queryKey, progress);
        setSteps(progress.steps ?? []);

        const newSections = buildGegevensSections(progress);
        setSections(newSections);

        // Show the welcome card only when there is no data yet AND the school has not already
        // pressed "Beginnen" (so a post-save refresh never makes the welcome reappear).
        const hasAnyData = newSections.some((s) => isCompleted(s.status) || s.status === "in_progress");
        setShowWelcome(!hasAnyData && !welcomeDismissed.current);
      }
    } catch (e) {
      console.error("Failed to load portal data", e);
      setLoadError(true);
    }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { refreshSections(); }, [refreshSections]);

  // Idle-prefetch the overview so returning to the Overzicht tab is instant.
  useEffect(() => {
    const t = setTimeout(() => prefetchOverview(schoolId), 800);
    return () => clearTimeout(t);
  }, [schoolId]);

  // After a save: refresh the stepper AND invalidate the school cache (bootstrap, overview,
  // etc.) so the "pending changes" state, the stepper status and the next-step are current
  // everywhere immediately. Short debounce so it feels instant but still coalesces rapid
  // auto-saves. (Editing must never leave a stale "aangeleverd"/done state visible.)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveState === "saved") {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => { refreshSections(); invalidateSchool(schoolId); }, 250);
    }
    return undefined;
  }, [saveState, refreshSections, schoolId]);

  // Canonical section: always redirect to the right place
  const activeSection = useMemo(() => {
    if (loading || sections.length === 0) return null;
    return getCanonicalSection(sections, requestedSection);
  }, [loading, sections, requestedSection]);

  useEffect(() => {
    if (loading || !activeSection) return;
    const canonicalPath = `/school/${schoolId}/schoolgegevens/${activeSection}`;
    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [loading, activeSection, schoolId, location.pathname, navigate]);

  const handleOpenSection = useCallback(
    (code: SectionCode) => {
      if (!canOpenSection(sections, code)) return;
      navigate(`/school/${schoolId}/schoolgegevens/${code}`);
    },
    [sections, navigate, schoolId],
  );

  const goToNextSection = useCallback(() => {
    const next = getNextRequired(sections);
    if (next) navigate(`/school/${schoolId}/schoolgegevens/${next}`);
  }, [sections, navigate, schoolId]);

  const completeSection = useCallback(() => {
    // Pure navigation: autosave already persisted the data, and the only real "aanleveren"
    // is on Controleren. Go to the next section in order (or the overview at the end).
    const idx = sections.findIndex((s) => s.code === activeSection);
    const next = idx >= 0 ? sections[idx + 1] : undefined;
    navigate(next ? `/school/${schoolId}/schoolgegevens/${next.code}` : `/school/${schoolId}/overzicht`);
  }, [activeSection, sections, schoolId, navigate]);

  const getSectionLock = useCallback((apiCode: string): SectionLock => {
    const step = steps.find((s) => s.sectionCode === apiCode);
    const opensAt = step?.opensAt ? +new Date(step.opensAt) : null;
    // Mirror the server's requireSectionOpen: schools edit and resubmit freely, so the
    // only lock is a section that has not opened yet. Closing/locking and the
    // request-to-reopen flow have been removed.
    const locked = !!(opensAt && Date.now() < opensAt);
    return { locked, reason: locked ? "not_open" : null };
  }, [steps]);

  // The per-section button just moves to the next step (Doorgaan). It is not a "confirm":
  // each section keeps its own disabled-state for completeness (isConfirm stays true so that
  // gate keeps applying), and the real aanlevering happens once on Controleren.
  const primaryAction = useMemo(() => {
    const idx = sections.findIndex((s) => s.code === activeSection);
    const next = idx >= 0 ? sections[idx + 1] : undefined;
    const label = !next ? "Naar overzicht" : next.code === "controleren" ? "Naar Controleren" : "Doorgaan";
    return { label, onClick: completeSection, isConfirm: true };
  }, [sections, activeSection, completeSection]);

  // Loading state — keep the header/nav visible (no full-screen reset); only the body
  // shows a skeleton. The school name comes from the cached bootstrap, so it is instant.
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PortalNav schoolName={bootstrap?.school.schoolName ?? schoolInfo?.schoolName} schoolId={schoolId} saveLabel={null} saveError={false} />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="mb-6 space-y-2">
              <div className="h-6 w-48 rounded bg-muted" />
              <div className="h-4 w-96 max-w-full rounded bg-muted" />
            </div>
            <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
              <div className="space-y-4">
                {[0, 1, 2, 3].map((i) => <div key={i} className="h-5 w-32 rounded bg-muted" />)}
              </div>
              <div className="h-64 rounded-lg bg-muted" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="mx-auto max-w-md px-6 text-center">
          <h1 className="text-lg font-bold text-foreground">Kan de gegevens niet laden</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Controleer uw internetverbinding en probeer het opnieuw.
          </p>
          <div className="mt-4 flex justify-center gap-3">
            <Button
              size="lg"
              onClick={() => { setLoading(true); refreshSections(); }}
            >
              Opnieuw proberen
            </Button>
            <Button variant="outline" size="lg" onClick={logout}>
              Uitloggen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Auto-save feedback lives in the SaveToast (bottom-right), which debounces its own spinner.

  return (
    <PortalContext.Provider
      value={{ schoolId, schoolInfo, phase, sections, activeSection, saveState, lastSavedAt, setSaveState, setLastSavedAt, refreshSections, goToNextSection, completeSection, getSectionLock, primaryAction }}
    >
      <div className="min-h-screen bg-background">
        {/* Header with nav */}
        <PortalNav
          schoolName={schoolInfo?.schoolName ?? bootstrap?.school.schoolName}
          schoolId={schoolId}
          saveLabel={null}
          saveError={false}
        />
        <SaveToast saveState={saveState} lastSavedAt={lastSavedAt} />

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Welcome card for first-time users */}
          {showWelcome ? (
            <div className="mx-auto max-w-lg py-16 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10">
                <LayersIcon className="text-primary" size={20} />
              </div>
              <h1 className="mt-4 text-xl font-semibold tracking-tight text-foreground">Welkom bij Mijn KUVO Woerden</h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                U levert de schoolgegevens stap voor stap aan. U hoeft niet alles in een keer te doen; uw wijzigingen worden automatisch opgeslagen.
              </p>
              <Button className="mt-6" onClick={() => { welcomeDismissed.current = true; setShowWelcome(false); }}>
                Beginnen
              </Button>
            </div>
          ) : (
            <>
            <PortalHeader title="Schoolgegevens aanleveren" subtitle="Contactpersonen, groepen en beschikbaarheid van uw school." />
            <div className="grid gap-8 max-lg:grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
              {/* Sidebar — vertical stepper */}
              <nav className="lg:sticky lg:top-8 lg:self-start" aria-label="Stappen">
                <ol className="space-y-0">
                  {sections.map((section, index) => {
                    const isActive = section.code === activeSection;
                    const completed = isCompleted(section.status);
                    const ds = section.code === "controleren" ? controlerenDelivery : section.deliveryState;
                    const canOpen = canOpenSection(sections, section.code);
                    const clickable = canOpen || completed;
                    const isLast = index === sections.length - 1;
                    // SHARED icon rule (identical to the Programmaselectie stepper): GREEN check =
                    // complete + aangeleverd; BLUE check = complete but not yet aangeleverd; (o) dot
                    // = started-but-incomplete OR gewijzigd; ring = niets begonnen. Controleren has
                    // no "completeness" of its own, so it leans on its whole-flow delivery state
                    // (complete = delivered) which still resolves to green when aangeleverd.
                    const complete = section.code === "controleren" ? ds === "delivered" : completed;
                    const started = section.status === "in_progress";
                    const kind = stepIconKind(complete, ds, started || isActive);
                    const lineTone = kind === "green" ? "bg-emerald-600/40" : "bg-border";

                    return (
                      <li key={section.code} className="relative flex items-center gap-3 pb-7 last:pb-0">
                        {/* Step indicator */}
                        <div className="relative z-10 shrink-0">
                          {/* Vertical connector line — from bottom of this circle to top of next */}
                          {!isLast && (
                            <div className={`absolute left-1/2 top-full h-7 w-px -translate-x-1/2 ${lineTone}`} />
                          )}
                          <StepCircle kind={kind} active={isActive} canOpen={canOpen} dim={!isActive} changed={ds === "changed"} />
                        </div>
                        {/* Label — the active step keeps full emphasis; the rest are subtly dimmed. */}
                        <button
                          type="button"
                          aria-current={isActive ? "step" : undefined}
                          className={`flex flex-col items-start gap-1 text-left text-sm outline-none transition-colors focus-visible:underline ${
                            isActive
                              ? "font-semibold text-foreground"
                              : clickable
                                ? "cursor-pointer text-muted-foreground/70 hover:text-foreground"
                                : "text-muted-foreground/40"
                          }`}
                          onClick={clickable ? () => handleOpenSection(section.code) : undefined}
                          disabled={!clickable}
                        >
                          <span>{SECTION_SHORT_TITLES[section.code]}</span>
                          {/* Disambiguate the (o) dot: a gewijzigde sectie gets a short label so the
                              dot never carries "gewijzigd" alone (the circle's tooltip does the rest).
                              The pill sits on its own line below the label so a long label
                              keeps its line instead of wrapping mid-word. */}
                          {kind === "dot" && ds === "changed" && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">Gewijzigd</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </nav>

              {/* Content panel */}
              <section className="min-w-0">
                {activeSection && (
                  <>
                    <header className="mb-5">
                      <h2 className="text-base font-semibold text-foreground">
                        {SECTION_TITLES[activeSection]}
                      </h2>
                    </header>
                    <SectionLockBanner sectionCode={activeSection} />
                    {/* When a section has not opened yet, make the whole body read-only:
                        a disabled fieldset greys out every button/input inside. Staff read-only
                        preview disables it the same way (the server also blocks every write). */}
                    <fieldset
                      disabled={isPreview || getSectionLock(sectionToApiCode(activeSection)).locked}
                      className="m-0 min-w-0 border-0 p-0"
                    >
                      {renderSection(activeSection, schoolId)}
                    </fieldset>
                  </>
                )}
              </section>
            </div>
            </>
          )}
        </main>
      </div>
    </PortalContext.Provider>
  );
}

